import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface DashboardStats {
  users: number;
  businesses: number;
  apartments: number;
  jobs: number;
  courses: number;
  gigs: number;
  forumTopics: number;
  mediaPosts: number;
  listings: number;
  reports: number;
  pendingReports: number;
  monthlySignups: number[];
}

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

const formatNumber = (n: number) => n.toLocaleString('fr-FR');

async function countRows(table: string, eq?: { column: string; value: string }): Promise<number> {
  let query = supabase.from(table).select('id', { count: 'exact', head: true });
  if (eq) query = query.eq(eq.column, eq.value);
  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}

function buildMonthlySignups(createdAtList: string[]): number[] {
  const currentYear = new Date().getFullYear();
  const months = Array.from({ length: 12 }, () => 0);
  createdAtList.forEach((createdAt) => {
    const d = new Date(createdAt);
    if (d.getFullYear() === currentYear) {
      months[d.getMonth()] += 1;
    }
  });
  return months;
}

function OverviewSection() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [
        users,
        businesses,
        apartments,
        jobs,
        courses,
        gigs,
        forumTopics,
        mediaPosts,
        listings,
        reports,
        pendingReports,
        { data: profileRows },
      ] = await Promise.all([
        countRows('profiles'),
        countRows('businesses'),
        countRows('apartments'),
        countRows('jobs'),
        countRows('courses'),
        countRows('gigs'),
        countRows('forum_topics'),
        countRows('media_posts'),
        countRows('listings'),
        countRows('reports'),
        countRows('reports', { column: 'status', value: 'pending' }),
        supabase.from('profiles').select('created_at'),
      ]);

      const createdAtList = ((profileRows as { created_at: string }[] | null) ?? []).map(
        (r) => r.created_at,
      );

      setStats({
        users,
        businesses,
        apartments,
        jobs,
        courses,
        gigs,
        forumTopics,
        mediaPosts,
        listings,
        reports,
        pendingReports,
        monthlySignups: buildMonthlySignups(createdAtList),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les statistiques.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
          <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="text-center py-24">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-500/10">
          <i className="ri-bar-chart-2-line text-3xl text-primary-400" />
        </div>
        <p className="text-foreground-400 mb-6">{error || 'Aucune statistique disponible.'}</p>
        <button
          onClick={load}
          className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const mainCards = [
    { label: 'Utilisateurs', value: stats.users, icon: 'ri-user-3-line' },
    { label: 'Entreprises', value: stats.businesses, icon: 'ri-building-2-line' },
    { label: 'Annonces immobilières', value: stats.apartments, icon: 'ri-home-4-line' },
    { label: 'Emplois', value: stats.jobs, icon: 'ri-briefcase-line' },
  ];

  const contents = [
    { label: 'Médias', value: stats.mediaPosts, icon: 'ri-video-line' },
    { label: 'Cours', value: stats.courses, icon: 'ri-graduation-cap-line' },
    { label: 'Services', value: stats.gigs, icon: 'ri-service-line' },
    { label: 'Sujets forum', value: stats.forumTopics, icon: 'ri-discuss-line' },
    { label: 'Annonces', value: stats.listings, icon: 'ri-megaphone-line' },
  ];

  const maxSignup = Math.max(...stats.monthlySignups, 1);
  const hasSignups = stats.monthlySignups.some((v) => v > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground-50">Tableau de bord</h1>
          <p className="text-sm text-foreground-400">Vue d'ensemble de l'activité de la plateforme.</p>
        </div>
        <span className="text-xs text-foreground-400 bg-background-100 border border-background-200/70 px-3 py-1.5 rounded-full whitespace-nowrap">
          Données en temps réel
        </span>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {mainCards.map((stat) => (
          <div key={stat.label} className="bg-background-100 border border-background-200/70 rounded-xl p-5">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 mb-3">
              <i className={`${stat.icon} text-lg text-primary-400`} />
            </div>
            <span className="block font-heading font-bold text-2xl text-foreground-50">
              {formatNumber(stat.value)}
            </span>
            <span className="block text-xs text-foreground-400 mt-1">{stat.label}</span>
          </div>
        ))}
      </div>

      {/* Chart + contents */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Signups chart */}
        <div className="lg:col-span-2 bg-background-100 border border-background-200/70 rounded-xl p-5">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-semibold text-foreground-50">Inscriptions mensuelles</h2>
            <span className="text-xs text-foreground-400">{new Date().getFullYear()}</span>
          </div>
          {hasSignups ? (
            <div className="flex items-end justify-between gap-2 h-48">
              {stats.monthlySignups.map((value, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-primary-600 to-primary-400 transition-all hover:opacity-80"
                    style={{ height: `${(value / maxSignup) * 160}px` }}
                  />
                  <span className="text-[10px] text-foreground-400 whitespace-nowrap">{MONTHS[idx]}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-center">
              <div>
                <i className="ri-line-chart-line text-3xl text-foreground-500 mb-3 block" />
                <p className="text-sm text-foreground-400">Aucune inscription enregistrée cette année.</p>
              </div>
            </div>
          )}
        </div>

        {/* Contents breakdown */}
        <div className="bg-background-100 border border-background-200/70 rounded-xl p-5">
          <h2 className="font-heading font-semibold text-foreground-50 mb-6">Contenus publiés</h2>
          <div className="space-y-4">
            {contents.map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <span className="flex items-center gap-2.5 text-sm text-foreground-400">
                  <i className={`${item.icon} text-foreground-500`} />
                  {item.label}
                </span>
                <span className="text-sm font-semibold text-foreground-50">{formatNumber(item.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 flex items-center gap-4">
          <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500/10 shrink-0">
            <i className="ri-flag-2-line text-lg text-accent-400" />
          </div>
          <div>
            <span className="block font-bold text-xl text-foreground-50">{formatNumber(stats.reports)}</span>
            <span className="block text-xs text-foreground-400">Signalements au total</span>
          </div>
        </div>
        <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 flex items-center gap-4">
          <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-500/10 shrink-0">
            <i className="ri-hourglass-2-line text-lg text-secondary-300" />
          </div>
          <div>
            <span className="block font-bold text-xl text-foreground-50">{formatNumber(stats.pendingReports)}</span>
            <span className="block text-xs text-foreground-400">En attente de modération</span>
          </div>
        </div>
        <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 flex items-center gap-4">
          <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
            <i className="ri-shield-check-line text-lg text-primary-400" />
          </div>
          <div>
            <span className="block font-bold text-xl text-foreground-50">99.9%</span>
            <span className="block text-xs text-foreground-400">Disponibilité</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OverviewSection;